/**
 * Arborescence Property → Apartment → Tenant en accordéons éditables.
 *
 * Composants exportés :
 *   - PropertiesAccordion (orchestrateur de tous les Property)
 *   - PropertyAccordion (un immeuble + apartments)
 *   - ApartmentAccordion (un logement + tenants)
 *   - TenantRow (un locataire)
 *   - Detail (helper d'affichage)
 *
 * Extrait de ImmeubleDossierDetail.jsx (Batch #14.221).
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { User, MapPin, ChevronRight, ChevronDown, Users, Home, Plus, Pencil, Trash2, Mail, Phone } from "lucide-react";
import { PropertyEditModal, ApartmentEditModal, TenantEditModal } from "./Modals";
import { API, authH } from "@/api";


// ============================================================
function PropertiesAccordion({ dossierId, properties, onChange }) {
  const [propModal, setPropModal] = useState(null); // null | {mode, target?}

  const deleteProp = async (prop) => {
    if (!window.confirm(`Supprimer la propriété « ${prop.label || "sans nom"} » et tous ses appartements/locataires ?`)) return;
    try {
      const r = await axios.delete(
        `${API}/immeuble-dossiers/${dossierId}/properties/${prop.id}`,
        { headers: authH() },
      );
      onChange(r.data);
      toast.success("Propriété supprimée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="border border-emerald-200 bg-emerald-50/30 rounded-xl p-5" data-testid="immeuble-properties-section">
      <div className="flex items-start justify-between gap-2 mb-3">
        <h2 className="font-semibold text-emerald-900 flex items-center gap-2">
          <Home className="w-5 h-5 text-emerald-600" /> Propriétés déclarées
          <span className="text-xs font-normal text-emerald-700">
            ({properties.length} propriété{properties.length > 1 ? "s" : ""})
          </span>
        </h2>
        <button
          onClick={() => setPropModal({ mode: "new" })}
          className="text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1"
          data-testid="immeuble-property-add-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Ajouter une propriété
        </button>
      </div>

      {properties.length === 0 ? (
        <div className="text-center py-10 bg-white border border-dashed border-emerald-200 rounded-lg" data-testid="immeuble-properties-empty">
          <Home className="w-8 h-8 mx-auto text-emerald-300 mb-2" />
          <p className="text-sm text-slate-500">Aucune propriété déclarée pour ce dossier.</p>
          <p className="text-xs text-slate-400 mt-1">
            Cliquez sur « Ajouter une propriété » pour commencer.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {properties.map((p) => (
            <PropertyAccordion
              key={p.id}
              dossierId={dossierId}
              property={p}
              onChange={onChange}
              onEdit={() => setPropModal({ mode: "edit", target: p })}
              onDelete={() => deleteProp(p)}
            />
          ))}
        </div>
      )}

      {propModal && (
        <PropertyEditModal
          dossierId={dossierId}
          mode={propModal.mode}
          target={propModal.target}
          onClose={() => setPropModal(null)}
          onSaved={(updated) => {
            onChange(updated);
            setPropModal(null);
          }}
        />
      )}
    </div>
  );
}

function PropertyAccordion({ dossierId, property: p, onChange, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const [aptModal, setAptModal] = useState(null);
  const apts = p.apartments || [];
  const tenantCount = apts.reduce((s, a) => s + (a.tenants?.length || 0), 0);
  const fullAddr = [
    [p.civic_number, p.street].filter(Boolean).join(" "),
    p.city, p.province, p.postal_code,
  ].filter(Boolean).join(", ");

  return (
    <div className="bg-white border border-emerald-200 rounded-lg overflow-hidden" data-testid={`immeuble-property-${p.id}`}>
      <div className="flex items-stretch hover:bg-emerald-50/50 transition">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-3 px-4 py-3 flex-1 text-left min-w-0"
          data-testid={`immeuble-property-toggle-${p.id}`}
        >
          {open
            ? <ChevronDown className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            : <ChevronRight className="w-4 h-4 text-emerald-600 flex-shrink-0" />}
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-800 truncate">
              {p.label || "(Propriété sans nom)"}
            </div>
            {fullAddr && (
              <div className="text-xs text-slate-500 truncate inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {fullAddr}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {p.building_type && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{p.building_type}</span>
            )}
            {p.year_built && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{p.year_built}</span>
            )}
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{apts.length} apt</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 inline-flex items-center gap-1">
              <Users className="w-2.5 h-2.5" /> {tenantCount}
            </span>
          </div>
        </button>
        <div className="flex items-center gap-1 px-2 border-l border-emerald-100">
          <button
            onClick={onEdit}
            className="p-1.5 hover:bg-emerald-100 rounded text-emerald-700"
            title="Modifier"
            data-testid={`immeuble-property-edit-${p.id}`}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 hover:bg-rose-100 rounded text-rose-600"
            title="Supprimer"
            data-testid={`immeuble-property-delete-${p.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-emerald-100 px-4 py-3 bg-emerald-50/20 space-y-3" data-testid={`immeuble-property-body-${p.id}`}>
          {/* Détails property */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <Detail label="Nombre de logements" value={p.num_units} />
            <Detail label="Année construction" value={p.year_built} />
            <Detail label="Type" value={p.building_type} />
            <Detail label="Prix d'achat" value={p.purchase_price ? `${p.purchase_price.toLocaleString("fr-CA")} $` : null} />
          </div>
          {p.notes && (
            <div className="text-xs text-slate-600 italic bg-white border border-emerald-100 rounded p-2">
              Notes : {p.notes}
            </div>
          )}

          {/* Ajout apt */}
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-600 uppercase">Appartements ({apts.length})</span>
            <button
              onClick={() => setAptModal({ mode: "new" })}
              className="text-[11px] px-2.5 py-1 rounded bg-slate-700 text-white hover:bg-slate-800 inline-flex items-center gap-1"
              data-testid={`immeuble-apartment-add-btn-${p.id}`}
            >
              <Plus className="w-3 h-3" /> Ajouter un appartement
            </button>
          </div>

          {apts.length === 0 ? (
            <div className="text-xs text-slate-400 italic text-center py-3 bg-white/60 rounded border border-dashed border-emerald-200">
              Aucun appartement déclaré.
            </div>
          ) : (
            <div className="space-y-1.5" data-testid={`immeuble-apartments-${p.id}`}>
              {apts.map((a) => (
                <ApartmentAccordion
                  key={a.id}
                  dossierId={dossierId}
                  propId={p.id}
                  apartment={a}
                  onChange={onChange}
                  onEdit={() => setAptModal({ mode: "edit", target: a })}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {aptModal && (
        <ApartmentEditModal
          dossierId={dossierId}
          propId={p.id}
          mode={aptModal.mode}
          target={aptModal.target}
          onClose={() => setAptModal(null)}
          onSaved={(updated) => {
            onChange(updated);
            setAptModal(null);
          }}
        />
      )}
    </div>
  );
}

function ApartmentAccordion({ dossierId, propId, apartment: a, onChange, onEdit }) {
  const [open, setOpen] = useState(false);
  const [tenantModal, setTenantModal] = useState(null);
  const tenants = a.tenants || [];

  const deleteApt = async () => {
    if (!window.confirm(`Supprimer l'appartement ${a.apt_number || ""} et tous ses locataires ?`)) return;
    try {
      const r = await axios.delete(
        `${API}/immeuble-dossiers/${dossierId}/properties/${propId}/apartments/${a.id}`,
        { headers: authH() },
      );
      onChange(r.data);
      toast.success("Appartement supprimé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden ml-3" data-testid={`immeuble-apartment-${a.id}`}>
      <div className="flex items-stretch hover:bg-slate-50 transition">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 px-3 py-2 flex-1 text-left min-w-0"
          data-testid={`immeuble-apartment-toggle-${a.id}`}
        >
          {open
            ? <ChevronDown className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
            : <ChevronRight className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />}
          <div className="text-sm font-semibold text-slate-800">
            Appartement {a.apt_number || "?"}
          </div>
          <div className="text-xs text-slate-500">
            {a.bedrooms != null && `· ${a.bedrooms} ch`}
            {a.monthly_rent != null && ` · ${a.monthly_rent.toLocaleString("fr-CA")} $/mois`}
            {a.surface_sqft != null && ` · ${a.surface_sqft} pi²`}
          </div>
          <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 inline-flex items-center gap-1">
            <Users className="w-2.5 h-2.5" /> {tenants.length}
          </span>
        </button>
        <div className="flex items-center gap-1 px-1.5 border-l border-slate-100">
          <button
            onClick={onEdit}
            className="p-1 hover:bg-slate-100 rounded text-slate-600"
            title="Modifier"
            data-testid={`immeuble-apartment-edit-${a.id}`}
          >
            <Pencil className="w-3 h-3" />
          </button>
          <button
            onClick={deleteApt}
            className="p-1 hover:bg-rose-100 rounded text-rose-600"
            title="Supprimer"
            data-testid={`immeuble-apartment-delete-${a.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-100 px-3 py-2 bg-slate-50/60 space-y-1.5" data-testid={`immeuble-apartment-body-${a.id}`}>
          {a.notes && (
            <div className="text-xs text-slate-600 italic">Notes : {a.notes}</div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-slate-600 uppercase">Locataires ({tenants.length})</span>
            <button
              onClick={() => setTenantModal({ mode: "new" })}
              className="text-[11px] px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1"
              data-testid={`immeuble-tenant-add-btn-${a.id}`}
            >
              <Plus className="w-3 h-3" /> Ajouter un locataire
            </button>
          </div>
          {tenants.length === 0 ? (
            <div className="text-xs text-slate-400 italic text-center py-2">
              Aucun locataire déclaré.
            </div>
          ) : (
            <div className="space-y-1" data-testid={`immeuble-tenants-${a.id}`}>
              {tenants.map((t) => (
                <TenantRow
                  key={t.id}
                  dossierId={dossierId}
                  propId={propId}
                  aptId={a.id}
                  tenant={t}
                  onChange={onChange}
                  onEdit={() => setTenantModal({ mode: "edit", target: t })}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {tenantModal && (
        <TenantEditModal
          dossierId={dossierId}
          propId={propId}
          aptId={a.id}
          mode={tenantModal.mode}
          target={tenantModal.target}
          onClose={() => setTenantModal(null)}
          onSaved={(updated) => {
            onChange(updated);
            setTenantModal(null);
          }}
        />
      )}
    </div>
  );
}

function TenantRow({ dossierId, propId, aptId, tenant: t, onChange, onEdit }) {
  const fullName = [t.first_name, t.last_name].filter(Boolean).join(" ") || "(Locataire sans nom)";

  const deleteTenant = async () => {
    if (!window.confirm(`Supprimer le locataire « ${fullName} » ?`)) return;
    try {
      const r = await axios.delete(
        `${API}/immeuble-dossiers/${dossierId}/properties/${propId}/apartments/${aptId}/tenants/${t.id}`,
        { headers: authH() },
      );
      onChange(r.data);
      toast.success("Locataire supprimé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded p-2" data-testid={`immeuble-tenant-${t.id}`}>
      <div className="flex items-start gap-2">
        <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
          <User className="w-3.5 h-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-800 truncate">{fullName}</div>
          <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
            {t.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {t.email}</span>}
            {t.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {t.phone}</span>}
            {t.move_in_date && <span>Emménagé : {t.move_in_date}</span>}
          </div>
          {t.notes && <div className="text-[11px] text-slate-500 italic mt-0.5">{t.notes}</div>}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onEdit}
            className="p-1 hover:bg-slate-100 rounded text-slate-600"
            title="Modifier"
            data-testid={`immeuble-tenant-edit-${t.id}`}
          >
            <Pencil className="w-3 h-3" />
          </button>
          <button
            onClick={deleteTenant}
            className="p-1 hover:bg-rose-100 rounded text-rose-600"
            title="Supprimer"
            data-testid={`immeuble-tenant-delete-${t.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold">{label}</div>
      <div className="text-sm text-slate-800">{value ?? <span className="text-slate-300">—</span>}</div>
    </div>
  );
}

// ============================================================
//  3 modales unifiées Create/Edit
//  Mode "new" : POST · Mode "edit" : PUT (champs pré-remplis depuis target)
// ============================================================

export { PropertiesAccordion, PropertyAccordion, ApartmentAccordion, TenantRow, Detail };
